import React, { useEffect, useId, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import { useT } from '../../i18n/LanguageContext';
import {
  DISTRIBUTOR_PASSWORD_PATTERN,
  DISTRIBUTOR_PASSWORD_REQUIREMENT,
} from '../../utils/distributorPassword';

const validExpiry = (value) =>
  typeof value === 'string' &&
  Number.isFinite(Date.parse(value)) &&
  new Date(value).toISOString() === value;
const unwrap = (response) => response?.data?.data;

export default function DistributorPreviewSetup({ distributor, admin }) {
  const t = useT();
  const passwordHelpId = useId();
  const { busy, setBusy, setError, setNotice, load } = admin;
  const [username, setUsername] = useState('clinicportal');
  const [password, setPassword] = useState('');

  const mounted = useRef(true);
  const pending = useRef(false);
  const selected = useRef(distributor);
  selected.current = distributor;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const submit = async (event, master) => {
    event.preventDefault();
    if (busy || pending.current || distributor.isActive !== true) return;
    if (master && (master.isActive !== true || !validExpiry(master.previewExpiresAt))) return;
    if (!master && !DISTRIBUTOR_PASSWORD_PATTERN.test(password)) {
      setError(DISTRIBUTOR_PASSWORD_REQUIREMENT);
      return;
    }
    pending.current = true;
    const session = captureSession();
    const id = distributor._id;
    const current = () =>
      mounted.current &&
      selected.current._id === id &&
      selected.current.isActive === true &&
      isSessionCurrent(session);
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const receipt = unwrap(
        master
          ? await distributorService.renewPreviewLogin(id, master._id, {
              expectedExpiresAt: master.previewExpiresAt,
            })
          : await distributorService.createPreviewLogin(id, {
              username: username.trim().toLowerCase(),
              password,
            })
      );
      if (!current()) return;
      if (
        !receipt?._id ||
        (master && receipt._id !== master._id) ||
        receipt.username !== (master?.username || username.trim().toLowerCase()) ||
        !validExpiry(receipt.expiresAt) ||
        typeof receipt.acceptancePending !== 'boolean'
      )
        throw new Error('Invalid preview receipt');
      if (!master) setPassword('');
      await load();
      if (!current()) return;
      setNotice(
        master
          ? `Read-only preview renewed for four hours. Sign in again with the same username and password. It expires ${new Date(receipt.expiresAt).toLocaleString()}.`
          : `Read-only preview login created. It expires ${new Date(receipt.expiresAt).toLocaleString()}.`
      );
    } catch {
      if (!current()) return;
      try {
        await load();
      } catch {
        /* Keep the uncertain result visible. */
      }
      if (!current()) return;
      setError(
        master
          ? 'Preview renewal could not be confirmed. Reload and review the current expiry before trying again. Sign in again if the expiry changed.'
          : 'Preview login could not be confirmed. Review existing logins before trying again.'
      );
    } finally {
      pending.current = false;
      // The parent owns this lock, including when selection changes unmount this panel.
      setBusy(false);
    }
  };

  return (
    <section className="me-distributor-admin-card space-y-4" aria-label="Read-only preview login">
      <div className="me-distributor-admin-card-heading">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Meeting preview login</h2>
          <p className="mt-1 text-sm text-slate-600">
            Create a four-hour, read-only login for {distributor.name}. It shows the current
            customer list and bank without allowing orders or account changes.
          </p>
        </div>
      </div>
      {distributor.masters
        .filter((master) => master.previewExpiresAt)
        .map((master) => (
          <div
            key={master._id}
            className="flex flex-wrap items-center gap-3 text-sm text-slate-600"
          >
            <p>
              {master.username} - expires {new Date(master.previewExpiresAt).toLocaleString()}
              {master.isActive !== true ? ' (inactive)' : ''}
            </p>
            {distributor.isActive === true &&
              master.isActive === true &&
              validExpiry(master.previewExpiresAt) && (
                <button
                  type="button"
                  disabled={busy}
                  className="me-distributor-admin-secondary"
                  onClick={(event) => submit(event, master)}
                  aria-label={`Renew ${master.username} for 4 hours`}
                >
                  Renew for 4 hours
                </button>
              )}
          </div>
        ))}
      <p className="text-sm text-slate-600">
        Renewal keeps the same username, password and legal acceptance. Existing sessions end; the
        preview user must sign in again. Preview logins never renew automatically.
      </p>
      <form onSubmit={submit} className="me-distributor-admin-form space-y-4">
        <fieldset
          disabled={busy || distributor.isActive !== true}
          className="grid gap-4 sm:grid-cols-2"
        >
          <label>
            Preview username
            <input
              required
              minLength={3}
              maxLength={32}
              pattern="[a-zA-Z][a-zA-Z0-9._-]{2,31}"
              autoComplete="off"
              className="mt-1 block w-full rounded border p-2"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
          </label>
          <label>
            Temporary password
            <input
              required
              type="password"
              autoComplete="new-password"
              minLength={12}
              maxLength={72}
              pattern={DISTRIBUTOR_PASSWORD_PATTERN.source}
              title={t(DISTRIBUTOR_PASSWORD_REQUIREMENT)}
              aria-describedby={passwordHelpId}
              className="mt-1 block w-full rounded border p-2"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          <p id={passwordHelpId} className="text-xs text-slate-600 sm:col-span-2">
            {t(DISTRIBUTOR_PASSWORD_REQUIREMENT)}
          </p>
        </fieldset>
        <button
          type="submit"
          disabled={busy || distributor.isActive !== true}
          className="me-distributor-admin-primary"
        >
          Create preview login
        </button>
      </form>
    </section>
  );
}
